import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useId, useState } from "react";
import { Link } from "react-router";

import { useOrganization } from "@/auth/organization";
import { ErrorAlert, Form, Modal, Field, Input, Button } from "@/components/ui";
import { domainKeys, domainsApi, longDate, type TransferCheck } from "@/lib/domains";
import { formatMoney } from "@/lib/format";
import { Hint } from "@/components/domains/parts";

/** Figma "Domain to transfer" (4090-9998), used here to check a code and add the transfer. */
export function TransferModal({
  open,
  onClose,
  onAdded,
  initialFqdn = "",
}: {
  open: boolean;
  onClose: () => void;
  /** Called with the name once the transfer is in the cart (the modal is already closed). */
  onAdded?: (fqdn: string) => void;
  /** A name to start with (resubmitting a rejected or cancelled transfer). Give the modal a `key` to change it. */
  initialFqdn?: string;
}) {
  const { orgId } = useOrganization();
  const qc = useQueryClient();
  const [fqdn, setFqdn] = useState(initialFqdn);
  const [code, setCode] = useState("");
  const [checked, setChecked] = useState<TransferCheck | null>(null);

  const check = useMutation({
    mutationFn: () => domainsApi.checkTransfer(orgId, fqdn.trim().toLowerCase(), code.trim()),
    onSuccess: setChecked,
  });
  const add = useMutation({
    mutationFn: () =>
      domainsApi.addToCart(orgId, {
        fqdn: fqdn.trim().toLowerCase(),
        kind: "transfer",
        auth_code: code.trim(),
      }),
    onSuccess: async () => {
      const added = fqdn.trim().toLowerCase();
      await qc.invalidateQueries({ queryKey: domainKeys.cart(orgId) });
      close();
      onAdded?.(added);
    },
  });

  function reset() {
    setChecked(null);
    check.reset();
    add.reset();
  }
  function close() {
    setFqdn("");
    setCode("");
    reset();
    onClose();
  }

  const ready = fqdn.trim().includes(".") && code.trim().length > 0;
  const valid = checked?.valid === true;
  const formId = useId();

  return (
    <Modal
      open={open}
      onClose={close}
      title="Domain to transfer"
      subtitle="Get the code from your current registrar's domain settings"
      note="Transfers usually finish within 5–7 days. Your website and email keep working."
      footer={
        <>
          <Button variant="secondary" size="lg" onClick={close}>
            Cancel
          </Button>
          {valid ? (
            <Button variant="brand" size="lg" type="submit" form={formId} loading={add.isPending}>
              Add transfer to cart
            </Button>
          ) : (
            <Button variant="brand" size="lg" type="submit" form={formId} loading={check.isPending}>
              Check code
            </Button>
          )}
        </>
      }
    >
      <Form
        id={formId}
        className="flex flex-col gap-4"
        error={check.error ?? add.error}
        validate={() => ({
          fqdn: fqdn.trim() !== "" && !fqdn.includes(".") && "Enter the full domain, like yourbusiness.co.uk",
        })}
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) add.mutate();
          else if (ready) check.mutate();
        }}
      >
        <Field size="lg" label="Domain" name="fqdn" required>
          {(control) => (
            <Input
              size="lg"
              {...control}
              value={fqdn}
              // eslint-disable-next-line jsx-a11y/no-autofocus -- the form is this screen's only task, so focus starts in its first field (PHP-3348)
              autoFocus
              autoComplete="off"
              spellCheck={false}
              placeholder="yourbusiness.co.uk"
              onChange={(e) => {
                setFqdn(e.target.value);
                reset();
              }}
            />
          )}
        </Field>
        <Field size="lg" label="Authorization code (EPP code)" name="auth_code" required>
          {(control) => (
            <Input
              size="lg"
              {...control}
              value={code}
              autoComplete="off"
              spellCheck={false}
              onChange={(e) => {
                setCode(e.target.value);
                reset();
              }}
            />
          )}
        </Field>
        <button type="submit" hidden />

        {checked === null ? (
          <Hint tone="warning" title="Keep this code private">
            Anyone with this code can move the domain. We only use it to ask your current registrar to release
            it.
          </Hint>
        ) : valid ? (
          <Hint tone="success" title="Code accepted">
            {checked.price_cents !== null
              ? `Transfer price ${formatMoney(checked.price_cents, checked.currency)} + VAT — includes a year added to the registration.`
              : "We'll confirm the transfer price at checkout."}
            {checked.current_registrar ? ` Currently at ${checked.current_registrar}.` : ""}
            {checked.expires_at ? ` Expires ${longDate(checked.expires_at)}.` : ""}
          </Hint>
        ) : (
          <Hint tone="danger" title="That code isn't right for this domain">
            Copy it again from your current registrar, and make sure the domain is unlocked there.
          </Hint>
        )}
        <ErrorAlert error={check.error ?? add.error} />
        <p className="text-xs text-fg-subtle">
          Moving several domains?{" "}
          <Link to="/domains/transfer/bulk" className="font-semibold text-brand-500 hover:underline">
            Transfer up to 100 at once
          </Link>
        </p>
      </Form>
    </Modal>
  );
}
